import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import { v4 } from "uuid";
import { WEB_FORMS_LOADING } from "../../constants/loading.constant";
import { COLOR_PRIMARY } from "../../constants/theme";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useWebFormCore } from "../../cores/webForm";
import { useIntl } from "react-intl";
import { ContactInputType } from "../../cores/webForm/constants/InputType.constant";
import { PositionType } from "../../cores/webForm/constants/PositionType.constant";
import FormCard from "../component/forms/web/FormCard";
import FormNameModal from "../component/forms/web/modal/FormNameModal";
import { NoData } from "../component/NoData";
import { UpgradePlanButton } from "../component/plan/UpgradePlanButton";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useGlobalCore } from "../../cores/globals";
import { WEBFORM_CONFIG } from "../../constants/modal.constant";
import { Action } from "../../cores/webForm/constants/Action.constant";

export default function WebForms() {
  const intl = useIntl();
  const { company, companyRole, isAdmin, isReseller, companyPlan } = useMeCore();
  const { turnOnModal, turnOffModal, isModal, getModalVisibility } = useGlobalCore();
  const { webForms, postWebForm, getWebForms, deleteWebForm, copyWebForm } =
    useWebFormCore();
  const { goToEditWebForm, goToSocial } = useRouterCore();

  useEffect(() => {
    if (
      companyPlan?.rules.webFormsAmount === 0 || (
        !companyRole?.forms &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller
      )
    ) goToSocial();
  }, [companyPlan, goToSocial, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    getWebForms(company?.id!);
  }, [company, getWebForms]);

  useEffect(() => {}, [webForms]);

  const toggleSideModal = useCallback(() => {
    turnOnModal(WEBFORM_CONFIG, true);
  }, [turnOnModal]);

  const handleDeleteWebForm = useCallback(
    async (webForm) => {
      await deleteWebForm(webForm.companyId, webForm.id);
      await getWebForms(webForm.companyId);
    },
    [deleteWebForm, getWebForms]
  );

  const handleEditWebForm = useCallback(
    async (webForm) => {
      goToEditWebForm(`${webForm.id}`);
    },
    [goToEditWebForm]
  );

  const handleCopyWebForm = useCallback(
    async (webForm) => {
      await copyWebForm(webForm.companyId, webForm.id);
    },
    [copyWebForm]
  );

  const canCreate = useMemo(
    () => companyPlan && webForms.length < companyPlan?.rules.webFormsAmount,
    [companyPlan, webForms.length]
  );

  const handleCreateWebForm = useCallback(
    async (name) => {
      await postWebForm(
        name,
        "Formulario de Contacto",
        40,
        "#555555",
        COLOR_PRIMARY,
        100,
        PositionType.MIDDLE.id,
        "Enviar",
        "#ffffff",
        15,
        "",
        "Gracias por dejarnos tu mensaje!",
        Action.MESSAGE,
        [
          {
            id: v4(),
            type: ContactInputType.CONTACT_NAME.id,
            placeholder: "Nombre completo",
            required: true,
          },
          {
            id: v4(),
            type: ContactInputType.CONTACT_EMAIL.id,
            placeholder: "Email",
            required: true,
          },
          {
            id: v4(),
            type: ContactInputType.CONTACT_PHONE.id,
            placeholder: "Teléfono",
            required: true,
          },
        ],
        company?.id!
      );
    },
    [postWebForm, company]
  );

  return (
    <Layout>
      <Header2 title="" mb={false} />
      <Content className="web-forms-page">
        <Container fluid>
          <div className="web-forms-hero">
            <div className="web-forms-hero-top">
              <div className="web-forms-hero-title-wrap">
                <h2 className="web-forms-hero-title mb-0">
                  {intl.formatMessage({ id: "app.advertisements.formWebTitle" })}
                </h2>
              </div>
              <div className="d-flex align-items-center flex-wrap justify-content-end">
                {!canCreate && <UpgradePlanButton />}
                {canCreate && webForms.length !== 0 && (
                  <Button
                    type="button"
                    className="web-forms-create-btn ml-2 mt-2 mt-sm-0"
                    onClick={toggleSideModal}
                  >
                    {intl.formatMessage({ id: "app.advertisements.formWeb" })}
                  </Button>
                )}
              </div>
            </div>
            <p className="web-forms-hero-subtitle mb-0">
              {intl.formatMessage({
                id: "app.advertisements.formWebHeroSubtitle",
              })}
            </p>
          </div>
        <UpdateLoadingCard
          loadingId={WEB_FORMS_LOADING}
          text={intl.formatMessage({ id: "app.advertisements.formWebUpdating" })} 
        />
        {!webForms.length && (
          <NoData
            image="generic1"
            text={intl.formatMessage({ id: "app.advertisements.formWebNotHaveCompany" })} 
            actionLabel={intl.formatMessage({ id: "app.advertisements.formWeb" })} 
            action={toggleSideModal}
            horizontal
          />
        )}
          <Row className="web-forms-grid">
            {webForms.map((webForm) => (
              <Col lg="3" md="4" sm="6" xs="12" key={webForm.id} className="mb-4">
                <FormCard
                  webForm={webForm}
                  canCreate={canCreate!}
                  onEdit={() => handleEditWebForm(webForm)}
                  onDelete={() => handleDeleteWebForm(webForm)}
                  onCopy={() => handleCopyWebForm(webForm)}
                />
              </Col>
            ))}
          </Row>
        </Container>
      </Content>
      <SideModal
        active={isModal(WEBFORM_CONFIG)}
        visible={getModalVisibility(WEBFORM_CONFIG)}
        onClose={() => turnOffModal(WEBFORM_CONFIG)}
      >
        <FormNameModal
          onSubmit={(name) => {
            handleCreateWebForm(name);
            turnOffModal(WEBFORM_CONFIG);
          }}
        />
      </SideModal>
    </Layout>
  );
}
